import type { CityConfig } from "@/pages/city-template/types";

export const waterburyConfig: CityConfig = {
  city: "Waterbury",
  state: "Connecticut",
  stateAbbr: "CT",
  slug: "waterbury",
  route: "/cities/waterbury",
  title: "Expedited Passport Services in Waterbury, CT | Fast Passport Center",
  metaDescription:
    "Get your passport fast in Waterbury, CT. Fast Passport Center offers 24-hour to 10-day expedited passport processing. Registered courier serving Waterbury, Naugatuck, Watertown, and all of the Naugatuck Valley.",
  h1: "Expedited Passport Services in Waterbury, CT",
  heroSubtitle:
    "Waterbury travelers rely on Fast Passport Center for emergency and expedited passports — from 24-hour emergency turnaround to 10-day Smart Saver. No need to visit the New York Passport Agency.",
  heroImageUrl:
    "https://readdy.ai/api/search-image?query=Golden%20hour%20aerial%20view%20of%20Waterbury%20Connecticut%20downtown%20with%20the%20iconic%20clock%20tower%20and%20Union%20Station%20building%2C%20Naugatuck%20Valley%20cityscape%2C%20warm%20amber%20lighting%20with%20historic%20architecture%2C%20rolling%20hills%20and%20river%20in%20background%2C%20professional%20travel%20photography%2C%20no%20text%20overlay%2C%20no%20people%20visible&width=1600&height=700&seq=ct-wat-hero-001&orientation=landscape",
  heroImageAlt: "Downtown Waterbury Connecticut at golden hour",
  geoPosition: "41.5582; -73.0515",
  geoPlacename: "Waterbury, Connecticut, United States",
  lastModified: "2026-06-01",
  regionalAgency: {
    name: "New York Passport Agency",
    address: "376 Hudson Street, New York, NY 10014",
    distance: "~90 miles from Waterbury",
  },
  nearbyCitiesZips: [
    { name: "Waterbury", zips: "06701, 06702, 06703, 06704, 06705, 06706, 06708, 06710, 06720, 06721, 06722, 06723, 06724, 06725, 06726, 06749" },
    { name: "Naugatuck", zips: "06770" },
    { name: "Watertown", zips: "06779, 06795" },
    { name: "Middlebury", zips: "06762" },
    { name: "Cheshire", zips: "06408, 06410, 06411" },
    { name: "Wolcott", zips: "06716" },
    { name: "Prospect", zips: "06712" },
    { name: "Bethlehem", zips: "06751" },
    { name: "Woodbury", zips: "06798" },
    { name: "Southbury", zips: "06488" },
    { name: "Seymour", zips: "06483" },
    { name: "Ansonia", zips: "06401" },
    { name: "Derby", zips: "06418" },
    { name: "Shelton", zips: "06484, 06485" },
    { name: "Oxford", zips: "06478" },
    { name: "Thomaston", zips: "06787" },
    { name: "Plymouth", zips: "06782, 06786" },
    { name: "Bristol", zips: "06010, 06011" },
    { name: "Meriden", zips: "06450, 06451" },
    { name: "New Britain", zips: "06050, 06051, 06052, 06053" },
  ],
  benefits: [
    {
      icon: "ri-time-line",
      title: "As Fast as 24 Hours",
      desc: "Emergency processing available for Waterbury travelers with urgent departure dates.",
    },
    {
      icon: "ri-shield-check-line",
      title: "Registered Courier",
      desc: "U.S. Department of State registered passport courier with reserved agency submission slots.",
    },
    {
      icon: "ri-customer-service-2-line",
      title: "Real Human Support",
      desc: "Dedicated agents guide you through every step — no chatbots, no automated runaround.",
    },
    {
      icon: "ri-map-pin-line",
      title: "Serving Naugatuck Valley",
      desc: "Waterbury, Naugatuck, Watertown, Middlebury, Cheshire, and all surrounding Naugatuck Valley communities.",
    },
  ],
  testimonials: [
    {
      name: "David P.",
      location: "Waterbury, CT",
      rating: 5,
      text: "I had a business trip to Germany in 4 days and my passport was expired. Fast Passport Center saved the day with their 3-day expedited service. Waterbury residents can trust them.",
      service: "Emergency Passport",
    },
    {
      name: "Maria L.",
      location: "Naugatuck, CT",
      rating: 5,
      text: "Renewed my passport for a cruise to the Caribbean. Fast Passport Center made it easy and had it back to Naugatuck in 5 days. Great Naugatuck Valley service.",
      service: "Passport Renewal",
    },
    {
      name: "James R.",
      location: "Watertown, CT",
      rating: 5,
      text: "Applied for my first passport through Fast Passport Center. They walked me through every step and I had it in under a week. Excellent for Watertown residents.",
      service: "New Passport",
    },
    {
      name: "Susan K.",
      location: "Middlebury, CT",
      rating: 5,
      text: "Needed a name change after marriage. Fast Passport Center handled the paperwork and I had my updated passport in 4 days. Professional Middlebury service.",
      service: "Name Change",
    },
  ],
  facilities: [
    {
      id: "wat-1",
      name: "Waterbury Main Post Office",
      type: "Post Office / Passport Acceptance Facility",
      address: "250 Grand Street",
      city: "Waterbury",
      state: "CT",
      zip: "06702",
      phone: "203-596-4210",
      hours: "Mon-Fri 9:00 AM - 5:00 PM, Sat 9:00 AM - 12:00 PM",
      distance: "Downtown Waterbury",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Passport acceptance services available by appointment. First-time applicants and those not eligible for mail-in renewal must apply in person.",
      icon: "ri-building-2-line",
    },
    {
      id: "wat-2",
      name: "Waterbury City Clerk",
      type: "City Clerk / Passport Services",
      address: "235 Grand Street",
      city: "Waterbury",
      state: "CT",
      zip: "06702",
      phone: "203-574-6780",
      hours: "Mon-Fri 8:30 AM - 4:30 PM",
      distance: "Waterbury City Center",
      website: "https://www.waterburyct.org/",
      websiteLabel: "City of Waterbury",
      notes: "Provides passport application acceptance services. Appointment recommended for all passport services.",
      icon: "ri-government-line",
    },
    {
      id: "wat-3",
      name: "Naugatuck Post Office",
      type: "Post Office / Passport Acceptance Facility",
      address: "58 North Main Street",
      city: "Naugatuck",
      state: "CT",
      zip: "06770",
      phone: "203-723-1425",
      hours: "Mon-Fri 9:00 AM - 5:00 PM, Sat 9:00 AM - 12:00 PM",
      distance: "Naugatuck",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Passport photo services and application acceptance available. Appointment required for passport applications.",
      icon: "ri-file-shield-line",
    },
    {
      id: "wat-4",
      name: "Cheshire Post Office",
      type: "Post Office / Passport Acceptance Facility",
      address: "88 Highland Avenue",
      city: "Cheshire",
      state: "CT",
      zip: "06410",
      phone: "203-272-5419",
      hours: "Mon-Fri 9:00 AM - 5:00 PM, Sat 9:00 AM - 12:00 PM",
      distance: "Cheshire",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Passport application acceptance services available by appointment.",
      icon: "ri-building-2-line",
    },
    {
      id: "wat-5",
      name: "Watertown Post Office",
      type: "Post Office / Passport Acceptance Facility",
      address: "60 Main Street",
      city: "Watertown",
      state: "CT",
      zip: "06795",
      phone: "860-945-4321",
      hours: "Mon-Fri 9:00 AM - 5:00 PM, Sat 9:00 AM - 12:00 PM",
      distance: "Watertown",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Passport application acceptance services available by appointment.",
      icon: "ri-file-shield-line",
    },
    {
      id: "wat-6",
      name: "Meriden Post Office",
      type: "Post Office / Passport Acceptance Facility",
      address: "89 North Colony Road",
      city: "Meriden",
      state: "CT",
      zip: "06450",
      phone: "203-238-5625",
      hours: "Mon-Fri 9:00 AM - 5:00 PM, Sat 9:00 AM - 12:00 PM",
      distance: "Meriden",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Passport application acceptance services available by appointment.",
      icon: "ri-government-line",
    },
  ],
  faqs: [
    {
      q: "How fast can I get a passport in Waterbury, CT?",
      a: "Fast Passport Center offers processing options from 24 hours to 10 business days. For emergency travel within 2 weeks, we recommend our 3-5 day or 24-hour emergency service. Standard processing through the post office takes 8-11 weeks.",
    },
    {
      q: "Do I need to visit the New York Passport Agency if I live in Waterbury?",
      a: "No — if you use Fast Passport Center, you don't need to visit the New York Passport Agency at all. We handle the agency submission for you as a registered courier. The only reason to visit the agency directly is for a documented life-or-death emergency, and appointments are extremely difficult to obtain.",
    },
    {
      q: "Where can I get passport photos in Waterbury?",
      a: "Passport photos are available at CVS Pharmacy, Walgreens, UPS Store, FedEx Office, and Walmart locations throughout Waterbury, Naugatuck, and Watertown. We also offer photo review as part of our service to ensure your photos meet all State Department requirements before submission.",
    },
    {
      q: "Can I renew my passport by mail from Waterbury?",
      a: "Yes, if your most recent passport was issued when you were 16 or older, within the last 15 years, and is undamaged. Mail renewal takes 8-11 weeks. For faster renewal, Fast Passport Center can expedite the process to as little as 3-5 days.",
    },
    {
      q: "What documents do I need for a first-time passport in Waterbury?",
      a: "You'll need proof of U.S. citizenship (birth certificate or naturalization certificate), a government-issued photo ID, two passport photos, and payment for government fees. Fast Passport Center provides a complete checklist and reviews your documents before submission.",
    },
    {
      q: "Does Fast Passport Center have an office in Waterbury?",
      a: "Fast Passport Center operates as a remote courier service — we do not have a physical office in Waterbury. We serve Waterbury and all of the Naugatuck Valley through our secure document shipping and agency submission process. Our headquarters are in Washington, DC.",
    },
    {
      q: "What is the nearest airport to Waterbury for international travel?",
      a: "Bradley International Airport (BDL) is the closest at ~35 miles. LaGuardia Airport (LGA) is ~85 miles, JFK International is ~90 miles, and Westchester County (HPN) is ~45 miles. Many Waterbury residents use Bradley or LaGuardia for international flights.",
    },
    {
      q: "How much does expedited passport service cost in Waterbury?",
      a: "Government fees are $130-$165 for adult passports, plus a $60 expedited fee. Fast Passport Center's service fees vary by processing speed: 10-day Smart Saver, 3-5 day Expedited, or 24-hour Emergency. Call 877-253-0084 for a personalized quote.",
    },
    {
      q: "Do both parents need to be present for a child passport in Waterbury?",
      a: "Yes, both parents or legal guardians must appear with the minor. If one parent cannot attend, a notarized DS-3053 Statement of Consent is required. Fast Passport Center provides guidance on all parental consent documentation.",
    },
    {
      q: "Can I get a passport the same day in Waterbury?",
      a: "Same-day passports are only available at regional passport agencies for documented life-or-death emergencies with proof of travel. Fast Passport Center's 24-hour emergency service is the fastest option available through a private courier.",
    },
    {
      q: "What should I do if my passport is lost or stolen in Waterbury?",
      a: "Report it immediately using Form DS-64 and apply for a replacement using Form DS-11. Fast Passport Center can help you file the report and process your replacement passport in 3-5 days.",
    },
    {
      q: "Does Waterbury have a local passport agency?",
      a: "No, Waterbury does not have a regional passport agency. The nearest regional agency is the New York Passport Agency in Manhattan, approximately 90 miles away. Fast Passport Center serves as your courier to this agency.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Waterbury", address: "various CVS locations in Waterbury", hours: "Mon-Sun, hours vary by location" },
    { name: "Walgreens — Waterbury", address: "various Walgreens locations in Waterbury", hours: "Mon-Sun, hours vary by location" },
    { name: "UPS Store — Waterbury", address: "various UPS Store locations in Waterbury", hours: "Mon-Fri 9:00 AM - 7:00 PM, Sat 10:00 AM - 4:00 PM" },
    { name: "FedEx Office — Waterbury", address: "various FedEx Office locations in Waterbury", hours: "Mon-Fri 8:00 AM - 9:00 PM, Sat-Sun 10:00 AM - 6:00 PM" },
    { name: "Walmart — Waterbury", address: "various Walmart locations in Waterbury", hours: "Mon-Sun 7:00 AM - 10:00 PM" },
  ],
  dmvLocations: [
    {
      name: "CT DMV — Waterbury",
      address: "62 Commercial Street, Waterbury, CT 06702",
      hours: "Mon-Fri 8:00 AM - 4:00 PM",
      note: "Full DMV services for license renewals and REAL ID applications.",
      featured: true,
    },
    {
      name: "CT DMV — Wethersfield",
      address: "60 State Street, Wethersfield, CT 06109",
      hours: "Mon-Fri 8:00 AM - 4:00 PM",
      note: "Main Connecticut DMV office. Accepts walk-ins but appointments are strongly recommended.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "BDL",
      name: "Bradley International Airport",
      distance: "~35 miles from Waterbury",
      address: "Schoephoester Rd, Windsor Locks, CT 06096",
      note: "Connecticut's primary airport serving all of New England. Offers domestic flights and international connections.",
      flightUrl: "https://www.bradleyairport.com/",
      flightLabel: "Bradley International",
      featured: true,
    },
    {
      code: "HPN",
      name: "Westchester County Airport",
      distance: "~45 miles from Waterbury",
      address: "240 Airport Road, Purchase, NY 10577",
      note: "Close regional airport with flights to major hubs. Convenient for Waterbury travelers.",
      flightUrl: "https://www.westchestergov.com/airport",
      flightLabel: "Westchester County Airport",
      featured: false,
    },
    {
      code: "JFK",
      name: "JFK International Airport",
      distance: "~90 miles from Waterbury",
      address: "JFK Access Rd, Queens, NY 11430",
      note: "One of the busiest international airports in the world with flights to virtually every global destination.",
      flightUrl: "https://www.jfkairport.com/",
      flightLabel: "JFK International",
      featured: false,
    },
  ],
  services: [
    {
      icon: "ri-passport-line",
      title: "Emergency Passport",
      description: "For Waterbury travelers with urgent departures within 24 hours to 2 weeks. Reserved agency submission with dedicated courier handling.",
      bullets: ["Processing as fast as 24 hours", "Dedicated agent assigned to your case", "Daily status updates"],
      badge: "Fastest",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Passport Renewal",
      description: "Fast renewal for Waterbury residents with a valid passport issued within the last 15 years at age 16 or older.",
      bullets: ["3-5 day or 10-day options available", "No acceptance clerk visit required", "Secure overnight document return"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-user-add-line",
      title: "New Passport",
      description: "First-time passport application assistance for Waterbury residents with step-by-step guidance through the acceptance clerk process.",
      bullets: ["Complete application review before submission", "Acceptance clerk appointment guidance", "3-5 day or 10-day processing options"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alarm-warning-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement service for Waterbury residents who have lost their passport or had it stolen.",
      bullets: ["DS-64 form guidance included", "Replacement in 3-5 days or 10 days", "Acceptance clerk visit required"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 in Waterbury, with parental consent guidance and acceptance clerk support.",
      bullets: ["Parental consent documentation review", "Both parents present or notarized consent", "3-5 day or 10-day processing available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal name change for Waterbury residents.",
      bullets: ["Marriage certificate or court order review", "Name change in 3-5 days or 10 days", "No clerk visit for most renewals"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for Waterbury travelers with immediate departure needs." },
    { name: "Passport Renewal", description: "Fast passport renewal for Waterbury residents with a recent valid passport." },
    { name: "New Passport", description: "First-time passport application assistance for Waterbury residents." },
    { name: "Lost or Stolen Passport", description: "Passport replacement for Waterbury residents who have lost their passport." },
    { name: "Child Passport", description: "Passport services for minors under 16 in Waterbury." },
    { name: "Name Change", description: "Passport name change service after marriage, divorce, or legal name change in Waterbury." },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Photos must be 2\"x2\" with a plain white or off-white background. Many Waterbury travelers get rejected at the acceptance clerk because their photos don't meet strict State Department specs.",
    },
    {
      icon: "ri-file-list-3-line",
      title: "Missing or Incorrect Forms",
      description: "Using the wrong form (DS-11 vs DS-82) or missing required fields can cause weeks of delays. We review every form before submission.",
    },
    {
      icon: "ri-calendar-line",
      title: "Waiting Too Long to Start",
      description: "Standard processing takes 8-11 weeks. Many Waterbury travelers wait until the last minute, leaving no buffer for problems.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Incorrect Payment Amount",
      description: "Government fees change frequently. Using an outdated check or wrong amount will cause your application to be returned.",
    },
    {
      icon: "ri-shield-line",
      title: "Damaged or Expired ID",
      description: "Your driver's license or state ID must be valid and unexpired. Many applicants don't realize their ID is about to expire until it's too late.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For child passports, both parents must appear or provide notarized consent. Missing documentation is the #1 reason child applications are delayed.",
    },
  ],
  steps: [
    {
      number: "01",
      icon: "ri-file-list-3-line",
      title: "Complete Your Application",
      description: "Fill out your passport application online or over the phone with our Naugatuck Valley team. We review everything for accuracy.",
    },
    {
      number: "02",
      icon: "ri-upload-cloud-line",
      title: "Send Us Your Documents",
      description: "Ship your documents via overnight courier to our secure processing center. We confirm receipt and begin review immediately.",
    },
    {
      number: "03",
      icon: "ri-search-line",
      title: "We Review Everything",
      description: "Our agents verify your forms, photos, and supporting documents meet all State Department requirements — no surprises.",
    },
    {
      number: "04",
      icon: "ri-shield-check-line",
      title: "Courier Submission",
      description: "As a registered U.S. Department of State courier, we submit your application with reserved agency processing slots.",
    },
    {
      number: "05",
      icon: "ri-passport-line",
      title: "Receive Your Passport",
      description: "Your passport is delivered securely to your Waterbury address. We provide tracking and daily status updates throughout.",
    },
  ],
  processingOptions: [
    {
      speed: "8-11",
      unit: "weeks",
      label: "Standard Processing",
      tag: "Post Office",
      tagColor: "bg-gray-200 text-gray-600",
      features: ["Mail-in application", "No expedited handling", "No dedicated agent", "No phone or email contact for your case", "Updates only by physical mail letter"],
      highlight: false,
      muted: true,
    },
    {
      speed: "10",
      unit: "business days",
      label: "Smart Saver",
      tag: "Fast Passport Center",
      tagColor: "bg-green-100 text-green-700",
      features: ["Secure overnight shipping included", "Document review before submission", "Dedicated agent assigned", "Daily status updates", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "3-5",
      unit: "business days",
      label: "Expedited Processing",
      tag: "Recommended",
      tagColor: "bg-[#1A365D] text-white",
      features: ["Secure overnight shipping included", "Document review before submission", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "24",
      unit: "hours",
      label: "Emergency Processing",
      tag: "Life or Death",
      tagColor: "bg-red-100 text-red-600",
      features: ["For documented life-or-death emergencies only", "Requires proof of emergency travel", "Strictly for documented life-or-death emergencies"],
      highlight: false,
      emergency: true,
    },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Must book weeks in advance", fpc: "No appointment needed — we handle agency access" },
    { feature: "Processing Speed", agency: "1-2 business days (if you get an appointment)", fpc: "As fast as 3-5 days or 24 hours for emergencies" },
    { feature: "Document Review", agency: "No pre-check — one mistake means restart", fpc: "Complete review before submission — we catch errors" },
    { feature: "Travel Proof", agency: "Requires printed itinerary with confirmed travel within 2 weeks", fpc: "Flexible documentation requirements" },
    { feature: "Updates", agency: "No phone or email support — only mail updates", fpc: "Daily status updates via phone and email" },
    { feature: "Guarantee", agency: "No guarantee of appointment or same-day processing", fpc: "Reserved agency slots with predictable processing" },
  ],
  stickyNavLinks: [
    { label: "Processing Options", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  internalLinks: [
    { name: "Waterbury", route: "/cities/waterbury" },
    { name: "New Haven", route: "/cities/new-haven" },
    { name: "Stamford", route: "/cities/stamford" },
    { name: "Bridgeport", route: "/cities/bridgeport" },
    { name: "Hartford", route: "/cities/hartford" },
    { name: "Danbury", route: "/cities/danbury" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Waterbury and all of the Naugatuck Valley as a remote courier service. We do not have a physical office in Waterbury, but our secure overnight shipping network ensures your documents reach our processing center quickly and safely.",
  fpcOfficeDistance: "Remote courier service — Waterbury, CT area",
  serviceAreaIntro: "Fast Passport Center provides expedited passport services to all Waterbury neighborhoods and surrounding Naugatuck Valley communities. From downtown Waterbury to Naugatuck, Watertown to Middlebury — we serve every Naugatuck Valley community with the same fast, reliable processing.",
  nearbyCitiesIntro: "We proudly serve Waterbury and all surrounding Naugatuck Valley communities. Whether you're in downtown Waterbury or a nearby suburb, our expedited passport services are available to you with the same speed and reliability.",
  howItWorksIntro: "Our Waterbury customers follow a simple, proven process to get their passports fast. No agency visits, no waiting in lines, no guessing.",
  mistakesIntro: "These are the most common mistakes Waterbury travelers make when applying for an expedited passport. Avoiding them saves weeks of delays.",
  testimonialsIntro: "Waterbury and Naugatuck Valley travelers trust Fast Passport Center for their urgent passport needs. Here's what they say about our service.",
  faqIntro: "Common questions about expedited passport services in Waterbury, CT. If you don't see your question here, call us at 877-253-0084.",
  statePageRoute: "/states/connecticut",
  statePageName: "Connecticut Passport Services",
};

export default waterburyConfig;